import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, FileSignature, X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray100: '#F3F4F6', gray500: '#6B7280',
  gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info: '#1E40AF', infoBg: '#DBEAFE',
  indigo: '#4338CA', indigoBg: '#EEF2FF',
};

const BN = {
  title: 'Kebenaran & e-Tandatangan', subtitle: 'rekod', back: '← Laman Utama',
  addConsent: '+ Tambah Kebenaran', tabs: ['Borang Kebenaran', 'Sejarah'] as const,
  allType: 'Semua Jenis', allStatus: 'Semua Status',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Rekod Kebenaran', emptyBody: 'Tiada borang kebenaran direkodkan.',
  addTitle: 'Tambah Borang Kebenaran',
  titleEn: 'Tajuk (EN) *', titleMs: 'Tajuk (MS)',
  consentType: 'Jenis Kebenaran *', description: 'Penerangan',
  deadlineDate: 'Tarikh Akhir', requiredClasses: 'Kelas Disasarkan',
  save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  acknowledge: 'Akui Terima',
  consentGiven: 'Bersetuju', consentDeclined: 'Tidak Bersetuju',
  totalForms: 'Jumlah Borang', acknowledged: 'Diakui', pending: 'Belum Selesai',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
  signedBy: 'Ditandatangani oleh', on: 'pada',
};
const EN = {
  title: 'Consent & e-Signature', subtitle: 'records', back: '← Home',
  addConsent: '+ Add Consent Form', tabs: ['Consent Forms', 'History'] as const,
  allType: 'All Types', allStatus: 'All Status',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No Consent Records', emptyBody: 'No consent forms recorded.',
  addTitle: 'Add Consent Form',
  titleEn: 'Title (EN) *', titleMs: 'Title (MS)',
  consentType: 'Consent Type *', description: 'Description',
  deadlineDate: 'Deadline Date', requiredClasses: 'Target Classes',
  save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  acknowledge: 'Acknowledge',
  consentGiven: 'Consent Given', consentDeclined: 'Consent Declined',
  totalForms: 'Total Forms', acknowledged: 'Acknowledged', pending: 'Pending',
  retry: 'Retry', errLoad: 'Failed to load.',
  signedBy: 'Signed by', on: 'on',
};

const CONSENT_TYPES = ['FIELD_TRIP','MEDICAL_PROCEDURE','DATA_SHARING','PHOTOGRAPHY','STUDENT_ACTIVITY','EMERGENCY_CONTACT','OTHER'];
const TYPE_CFG: Record<string, { ms: string; en: string; color: string; bg: string }> = {
  FIELD_TRIP:        { ms: 'Lawatan Sambil Belajar', en: 'Field Trip',           color: '#0F766E', bg: '#CCFBF1' },
  MEDICAL_PROCEDURE: { ms: 'Prosedur Perubatan',     en: 'Medical Procedure',    color: '#991B1B', bg: '#FEE2E2' },
  DATA_SHARING:      { ms: 'Perkongsian Data',        en: 'Data Sharing',         color: '#1E40AF', bg: '#DBEAFE' },
  PHOTOGRAPHY:       { ms: 'Fotografi',               en: 'Photography',          color: '#6D28D9', bg: '#EDE9FE' },
  STUDENT_ACTIVITY:  { ms: 'Aktiviti Pelajar',        en: 'Student Activity',     color: '#B45309', bg: '#FEF9C3' },
  EMERGENCY_CONTACT: { ms: 'Kenalan Kecemasan',       en: 'Emergency Contact',    color: '#BE185D', bg: '#FCE7F3' },
  OTHER:             { ms: 'Lain-lain',               en: 'Other',                color: '#4B5563', bg: '#F3F4F6' },
};
const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  ACTIVE:   { label: 'Aktif',      color: '#166534', bg: '#DCFCE7' },
  CLOSED:   { label: 'Ditutup',    color: '#4B5563', bg: '#F3F4F6' },
  EXPIRED:  { label: 'Tamat',      color: '#991B1B', bg: '#FEE2E2' },
};

interface Props { user: AuthUser }
interface ConsentForm {
  id: string; title_en: string; title_ms: string | null; consent_type: string;
  description: string | null; deadline_date: string | null; status: string;
  required_classes: string | null; created_at: string;
  total_responses: number; acknowledged_count: number;
}
interface AckRecord {
  id: string; form_title_en: string; form_title_ms: string | null;
  student_name: string; parent_name: string; acknowledged_at: string;
  consent_given: boolean; signature_hash: string;
}
interface PageResult { data: ConsentForm[]; total: number; page: number }
const LIMIT = 20;
const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';

export function ConsentPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit  = hasRole(user, 'super_admin', 'admin', 'principal', 'clerk');
  const schoolId = schoolIdFromUser(user) ?? '';

  const [tab,    setTab]    = useState<'forms' | 'history'>('forms');
  const [forms,  setForms]  = useState<ConsentForm[]>([]);
  const [acks,   setAcks]   = useState<AckRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [form, setForm] = useState({
    titleEn: '', titleMs: '', consentType: 'STUDENT_ACTIVITY',
    description: '', deadlineDate: '', requiredClasses: '',
  });

  const loadForms = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ schoolId, page: String(page) });
      if (typeFilter)   p.set('consentType', typeFilter);
      if (statusFilter) p.set('status', statusFilter);
      const res = await apiGet<PageResult>(`/api/consent/forms?${p}`);
      setForms(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, typeFilter, statusFilter, UI.errLoad]);

  const loadHistory = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setAcks(await apiGet<AckRecord[]>(`/api/consent/acknowledgements?schoolId=${schoolId}&page=${page}`));
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, UI.errLoad]);

  useEffect(() => { if (tab === 'forms') loadForms(); else loadHistory(); }, [tab, loadForms, loadHistory]);

  const submit = async () => {
    if (!form.titleEn.trim()) return;
    setSaving(true);
    try {
      await apiPost('/api/consent/forms', {
        titleEn: form.titleEn, titleMs: form.titleMs || undefined,
        consentType: form.consentType, description: form.description || undefined,
        deadlineDate: form.deadlineDate || undefined, requiredClasses: form.requiredClasses || undefined,
        schoolId,
      });
      setShowAdd(false);
      setForm({ titleEn: '', titleMs: '', consentType: 'STUDENT_ACTIVITY', description: '', deadlineDate: '', requiredClasses: '' });
      setToast({ message: lang === 'en' ? 'Consent form created.' : 'Borang kebenaran dicipta.', variant: 'success' });
      loadForms();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const acknowledge = async (formId: string, consentGiven: boolean) => {
    try {
      await apiPost(`/api/consent/forms/${formId}/acknowledge`, { consentGiven });
      setToast({ message: lang === 'en' ? 'Acknowledged.' : 'Diakui.', variant: 'success' });
      loadForms();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900, backgroundColor: C.white, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.indigoBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldCheck size={20} color={C.indigo} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && tab === 'forms' && <button onClick={() => setShowAdd(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>{UI.addConsent}</button>}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: UI.totalForms, value: total, color: C.indigo, bg: C.indigoBg },
          { label: UI.acknowledged, value: forms.reduce((s, f) => s + f.acknowledged_count, 0), color: C.success, bg: C.successBg },
          { label: UI.pending, value: forms.filter(f => f.status === 'ACTIVE').length, color: C.warning, bg: C.warningBg },
        ].map(card => (
          <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: card.color }}>{card.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        {(['forms', 'history'] as const).map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: tab === t ? C.black : C.gray500, borderBottom: tab === t ? `2px solid ${C.yellow}` : '2px solid transparent', marginBottom: '-1px' }}>
            {lang === 'en' ? EN.tabs[i] : BN.tabs[i]}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { val: typeFilter, set: setTypeFilter, opts: CONSENT_TYPES.map(t => ({ v: t, l: lang === 'en' ? TYPE_CFG[t]?.en ?? t : TYPE_CFG[t]?.ms ?? t })), placeholder: UI.allType },
          { val: statusFilter, set: setStatusFilter, opts: Object.keys(STATUS_CFG).map(s => ({ v: s, l: STATUS_CFG[s]!.label })), placeholder: UI.allStatus },
        ].map(({ val, set, opts, placeholder }) => (
          <select key={placeholder} value={val} onChange={e => { set(e.target.value); setPage(1); }} style={{ ...inputStyle, width: '200px' }}>
            <option value="">{placeholder}</option>
            {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
      {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}

      {tab === 'forms' && (
        <div style={{ display: 'grid', gap: '12px' }}>
          {!loading && forms.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <FileSignature size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
            </div>
          )}
          {forms.map(f => {
            const tc = TYPE_CFG[f.consent_type];
            const sc = STATUS_CFG[f.status] ?? STATUS_CFG['ACTIVE']!;
            const pct = f.total_responses > 0 ? Math.round((f.acknowledged_count / f.total_responses) * 100) : 0;
            return (
              <div key={f.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: C.gray900, marginBottom: '4px' }}>{lang === 'en' ? f.title_en : (f.title_ms ?? f.title_en)}</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: tc?.bg, color: tc?.color }}>{lang === 'en' ? tc?.en : tc?.ms}</span>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: sc.bg, color: sc.color }}>{sc.label}</span>
                      {f.deadline_date && <span style={{ fontSize: '12px', color: C.gray500 }}>📅 {new Date(f.deadline_date).toLocaleDateString('ms-BN')}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: C.indigo }}>{pct}%</div>
                    <div style={{ fontSize: '11px', color: C.gray500 }}>{f.acknowledged_count}/{f.total_responses}</div>
                  </div>
                </div>
                {f.description && <p style={{ margin: '0 0 12px', fontSize: '13px', color: C.gray700 }}>{f.description}</p>}
                <div style={{ height: '6px', background: C.gray100, borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: C.indigo, borderRadius: '3px', transition: 'width 0.3s' }} />
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => acknowledge(f.id, true)} style={{ padding: '5px 14px', borderRadius: '6px', background: C.successBg, border: 'none', color: C.success, fontWeight: 600, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle size={12} />{UI.consentGiven}
                  </button>
                  <button onClick={() => acknowledge(f.id, false)} style={{ padding: '5px 14px', borderRadius: '6px', background: C.errorBg, border: 'none', color: C.error, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>
                    {UI.consentDeclined}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'history' && (
        <div style={{ display: 'grid', gap: '10px' }}>
          {!loading && acks.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <CheckCircle size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>{lang === 'en' ? 'No acknowledgement records found.' : 'Tiada rekod pengakuan dijumpai.'}</p>
            </div>
          )}
          {acks.map(a => (
            <div key={a.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '14px 16px', boxShadow: cardShadow, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '14px', color: C.gray900 }}>{lang === 'en' ? a.form_title_en : (a.form_title_ms ?? a.form_title_en)}</div>
                <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px' }}>
                  {a.student_name} · {UI.signedBy} {a.parent_name} {UI.on} {new Date(a.acknowledged_at).toLocaleDateString('ms-BN')}
                </div>
                <div style={{ fontSize: '11px', color: C.gray500, fontFamily: 'monospace', marginTop: '2px' }}>
                  SHA: {a.signature_hash.substring(0, 16)}…
                </div>
              </div>
              <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: a.consent_given ? C.successBg : C.errorBg, color: a.consent_given ? C.success : C.error }}>
                {a.consent_given ? UI.consentGiven : UI.consentDeclined}
              </span>
            </div>
          ))}
        </div>
      )}

      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
          <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
            <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '500px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addTitle}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { key: 'titleEn', label: UI.titleEn, type: 'text' },
                { key: 'titleMs', label: UI.titleMs, type: 'text' },
                { key: 'deadlineDate', label: UI.deadlineDate, type: 'date' },
                { key: 'requiredClasses', label: UI.requiredClasses, type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.consentType}</label>
                <select value={form.consentType} onChange={e => setForm(p => ({ ...p, consentType: e.target.value }))} style={inputStyle}>
                  {CONSENT_TYPES.map(t => <option key={t} value={t}>{lang === 'en' ? TYPE_CFG[t]?.en : TYPE_CFG[t]?.ms}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{UI.description}</label>
                <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  style={{ ...inputStyle, height: '70px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
